import React, { useState, useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import { Typography as AntTypography, message } from "antd";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import moment from "moment";
import CallHistoryAudioPlayer from "./CallHistoryAudioPlayer";
import CallHistoryTranscript from "./CallHistoryTranscript";
import { SearchInput } from "../../callhistory/components";
import { getCallRecording, rescheduleCall } from "../../../utils/apiClient";
import { DateSelectionPopup } from "../../callhistory/components/DateSelectionPopup";

const { Text } = AntTypography;

/**
 * CallTranscriptView - Shows audio player with waveform and call transcript
 * Uses NEW CallHistoryAudioPlayer designed for Call History V2 data structure
 */
const CallTranscriptView = ({ callData, callRequestId, currentTheme, themeMode }) => {
  const [searchInput, setSearchInput] = useState("");
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [showDatePopup, setShowDatePopup] = useState(false);

  // Fetch audio URL when callData changes
  useEffect(() => {
    let isMounted = true;

    const fetchAudio = async () => {
      if (!callData?._id) return;

      setAudioUrl(null); // Reset audio URL
      setAudioLoading(true);

      try {
        const response = await getCallRecording(callData._id);
        if (isMounted && response.statusCode === "SUCCESS" && response.response?.signedUrl) {
          setAudioUrl(response.response.signedUrl);
        }
      } catch (error) {
        console.error("Error fetching audio:", error);
      } finally {
        if (isMounted) {
          setAudioLoading(false);
        }
      }
    };

    fetchAudio();

    return () => {
      isMounted = false; // Cleanup flag to prevent state updates on unmounted component
    };
  }, [callData?._id]); // Refetch when call ID changes

  const handleRescheduleClick = () => {
    setShowDatePopup(true);
  };

  const handleScheduleCall = async (requestBody) => {
    try {
      const { callRequestId, utcStartTime } = requestBody;
      if (!callRequestId || !utcStartTime) {
        message.error("Missing required parameters for scheduling call");
        return;
      }
      const response = await rescheduleCall({
        callRequestId,
        startDateTime: utcStartTime,
      });
      if (response.statusCode === "SUCCESS") {
        message.success("Call reattempt scheduled successfully!");
        setShowDatePopup(false);
      } else {
        message.error(response.message || "Failed to schedule call reattempt");
      }
    } catch (error) {
      console.error("Error scheduling call:", error);
      message.error("Failed to schedule call reattempt. Please try again.");
    }
  };

  const handleCloseDatePopup = () => {
    setShowDatePopup(false);
  };

  // Calculate call duration and date
  const callDuration = useMemo(() => {
    const totalSeconds = callData?.callDuration?.totalDuration || 0;
    if (totalSeconds === 0) return "0m 0s";
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}m ${secs}s`;
  }, [callData?.callDuration?.totalDuration]);

  const callDate = useMemo(() => {
    if (!callData?.callStartTime) return "Scheduled";
    
    const startDate = moment(callData.callStartTime);
    
    if (startDate.isSame(moment(), "day")) {
      return `Today ${startDate.format("hh:mm A")}`;
    } else if (startDate.isSame(moment().subtract(1, "day"), "day")) {
      return `Yesterday ${startDate.format("hh:mm A")}`;
    } else {
      return `${startDate.format("MM/DD/YYYY : hh:mm A")}`;
    }
  }, [callData?.callStartTime]);

  return (
    <Box
      sx={{
        backgroundColor: themeMode === "dark" ? "#242143" : "#ffffff",
        borderRadius: "16px",
        padding: 3,
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Search and metadata row */}
      <Box
        sx={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box display="flex" alignItems="center">
          <SearchInput
            className="audio-transcript-search"
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            width="40vw"
            placeholder="Search Transcript"
          />
        </Box>

        <Box display="flex" alignItems="center" gap={3}>
          <Box display="flex" alignItems="center">
            <CalendarTodayIcon sx={{ mr: 1, color: currentTheme.text.secondary }} />
            <Text
              style={{
                color: currentTheme.text.secondary,
                fontSize: 14,
                paddingRight: "0.5rem",
                marginRight: "0.5rem",
                borderRight: `1px solid ${currentTheme.text.secondary}`,
              }}
            >
              {callDate}
            </Text>
            <AccessTimeFilledIcon sx={{ ml: 2, mr: 1, color: currentTheme.text.secondary }} />
            <Text
              style={{
                color: currentTheme.text.secondary,
                fontSize: 14,
              }}
            >
              {callDuration}
            </Text>
          </Box>

          <Box
            onClick={handleRescheduleClick}
            sx={{
              px: 3,
              py: 1.5,
              backgroundColor: "#e63e2b",
              color: "#ffffff",
              borderRadius: "12px",
              cursor: "pointer",
              fontWeight: 500,
              fontSize: "0.95rem",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "#d32f2f",
              },
            }}
          >
            Reschedule This Call
          </Box>
        </Box>
      </Box>

      {/* Speaker statistics */}
      {/* {speakerStats.length > 0 && (
        <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
          <Typography sx={{ color: currentTheme.text.secondary, fontSize: "0.85rem" }}>
            <strong>Speakers:</strong>{" "}
            {speakerStats.map((stat, idx) => (
              <span key={idx}>
                {stat.speaker} ({stat.percentage}%)
                {idx < speakerStats.length - 1 ? ", " : ""}
              </span>
            ))}
          </Typography>
        </Box>
      )} */}

      {/* Audio Player Section */}
      <div style={{ 
        flex: 1, 
        overflow: "hidden", 
        display: "flex", 
        flexDirection: "column",
        minHeight: 0,
        marginBottom: "2vh"
      }}>
        {audioLoading ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '3vh',
            color: currentTheme.callhistory.text
          }}>
            <img
              src={
                themeMode === "dark"
                  ? "/icons/loader_white.gif"
                  : "/icons/loader_black.gif"
              }
              alt="Loading audio..."
              style={{ width: "3vh", height: "3vh" }}
            />
            <span style={{ marginLeft: '0.5rem' }}>Loading audio...</span>
          </div>
        ) : audioUrl ? (
          <CallHistoryAudioPlayer audioUrl={audioUrl} />
        ) : (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '3vh',
            color: currentTheme.callhistory.text
          }}>
            No audio available for this call
          </div>
        )}
      </div>

      {/* Transcript Section - Always render, regardless of audio availability */}
      <Box
        sx={{
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <CallHistoryTranscript 
          transcription={callData?.transcription || []}
          searchInput={searchInput}
          currentTime={0}
        />
      </Box>

      {/* Date Selection Popup for Rescheduling */}
      <DateSelectionPopup
        visible={showDatePopup}
        onClose={handleCloseDatePopup}
        callRequestId={callRequestId}
        onScheduleCall={handleScheduleCall}
      />
    </Box>
  );
};

export default CallTranscriptView;

